import { type ReactNode, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useLocation } from 'wouter';
import { UnifiedSidebar } from '@/components/sidebar';
import { AdminSectionSidebar } from '@/components/sidebar/AdminSectionSidebar';
import { GlobalHeader } from '@/components/GlobalHeader';
import { MobileTabBar } from '@/components/MobileTabBar';
import { UserMenu } from '@/components/sidebar/UserMenu';

interface AdminShellProps {
  children: ReactNode;
}

/**
 * AdminShell provides the shell for all Admin pages (AR-285).
 *
 * Structure: GlobalHeader → [ UnifiedSidebar (sections) | AdminSectionSidebar (pages) | content ]
 *
 * Mirrors MainLayout's auth handling and FinancesLayout's two-column
 * navigation, so the Admin area matches the rest of the app — including the
 * AR-316 phone shell: below `md` both rails leave the flow and their two levels
 * (admin sections, then the pages of the open section) move into the More sheet
 * above the bottom tab bar. Without this the rails ate the whole viewport and
 * pushed the page content off-screen.
 */
export default function AdminShell({ children }: AdminShellProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { favicon } = useTheme();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (favicon) {
      const faviconElement = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (faviconElement) {
        faviconElement.href = favicon;
      } else {
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = favicon;
        document.head.appendChild(newFavicon);
      }
    }
  }, [favicon]);

  // Redirect unauthenticated users (Admin has no public routes). Runs as a
  // committed side effect, not during render — same as FinancesLayout.
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (!isAuthenticated) {
    if (isLoading) {
      return <div>Loading...</div>;
    }
    return null;
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-[#101415] overflow-hidden">
      <GlobalHeader />
      <div className="flex flex-1 min-h-0">
        <div className="hidden md:flex">
          <UnifiedSidebar />
        </div>
        <div className="hidden md:flex">
          <AdminSectionSidebar />
        </div>
        <main
          className="flex-1 overflow-auto bg-white dark:bg-[#101415]"
          style={{ minHeight: 0 }}
        >
          {children}
        </main>
      </div>
      <MobileTabBar footer={<UserMenu expanded inline />}>
        {(close) => (
          <>
            <UnifiedSidebar inDrawer onNavigated={close} showUserMenu={false} />
            <AdminSectionSidebar inDrawer onNavigated={close} />
          </>
        )}
      </MobileTabBar>
    </div>
  );
}
